import React from "react";
import { useParams } from "react-router-dom";
import {
  CheckOutlined,
  ErrorOutline,
  ReplayOutlined,
} from "@mui/icons-material";
import { Grid, Box, Avatar, colors, Icon, Typography } from "@mui/material";
import { AppColors } from "../common/AppColors";
import { Images } from "../common/Images";
import Header from "../components/Header/Header";
import Footer from "../components/shared/Footer";

type PaymentStatusType =
  | "success"
  | "order_not_found"
  | "already_paid"
  | "error";

// Define message mappings
const paymentStatusConfig: Record<
  PaymentStatusType,
  {
    icon: React.ReactNode;
    color: string;
    title: string;
    message: string;
  }
> = {
  success: {
    icon: <CheckOutlined fontSize="large" />,
    color: colors.green[500],
    title: "Payment Successful",
    message:
      "Thank you for your payment. Your transaction has been completed successfully.",
  },
  order_not_found: {
    icon: <ErrorOutline fontSize="large" />,
    color: colors.red[500],
    title: "Order Not Found",
    message: "We couldn't find your order. Please verify the payment link",
  },
  already_paid: {
    icon: <ReplayOutlined fontSize="large" />,
    color: colors.orange[500],
    title: "Order Already Paid",
    message:
      "This order has already been paid. No further payment is required.",
  },
  error: {
    icon: <ErrorOutline fontSize="large" />,
    color: colors.red[700],
    title: "Payment Error",
    message:
      "Something went wrong while processing your payment. Please try again later.",
  },
};

export const PaymentResponsePage: React.FC = () => {
  const { status } = useParams<{ status: PaymentStatusType }>();
  const reason = status || "success";

  const { icon, color, title, message } =
    paymentStatusConfig[reason] ?? paymentStatusConfig.error;

  return (
    <>
      <Header />
      <Grid container spacing={2} justifyContent="center">
        <Box
          height="50vh"
          width="100%"
          sx={{
            backgroundImage: `url(${Images.windowsWallpaper})`,
            backgroundSize: "81% 190%",
          }}
          position="relative"
          display="flex"
          alignItems="center"
        >
          {/* Glassmorphism Background Box */}
          <Box
            bgcolor={AppColors.White}
            sx={{
              margin: "auto",
              left: "0",
              right: "0",
              position: "absolute",
              width: { xs: "90%", md: "60vw" },
              maxWidth: "700px",
              height: "35vh",
              opacity: "0.5",
              background: "rgba(255, 255, 255, 0.24)",
              borderRadius: "16px",
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
          ></Box>

          {/* Content Box */}
          <Box
            position="absolute"
            color={AppColors.White}
            textAlign="center"
            width={{ xs: "90%", md: "60vw" }}
            maxWidth="700px"
            left={0}
            right={0}
            margin="auto"
          >
            <Avatar
              sx={{
                bgcolor: color,
                margin: "auto",
                width: 56,
                height: 56,
              }}
            >
              {icon}
            </Avatar>
            <Typography variant="h4" mt={2}>
              {title}
            </Typography>
            <Typography variant="subtitle1" mt={1}>
              {message}
            </Typography>
            {reason === "success" && (
              <Typography variant="subtitle1">
                A confirmation email has been sent to your registered email
                address.
              </Typography>
            )}
          </Box>
        </Box>
      </Grid>
      <Footer />
    </>
  );
};
